import React, { Fragment, useMemo } from "react";
import { Dialog, Transition } from "@headlessui/react";
import {
  XMarkIcon,
  SparklesIcon,
  RocketLaunchIcon,
  PlusIcon,
  EllipsisHorizontalIcon,
} from "@heroicons/react/24/outline";
import { Link, useLocation, useHistory } from "react-router-dom";
import clsx from "clsx";
import { buildFlatNavigation, isNavActive } from "@/navigation";
import { useFeatureFlag, useProfile, useActivePromo } from "@moonscape/shared";
import { FEATURE_FLAG_KEYS } from "@moonscape/shared";
import { useReadOnly } from "@/contexts";
import { Lab, Logo } from "@/components/ms";

/** Shared: the role/feature-flag filtered nav list. */
const useNavigation = () => {
  const showIAP = useFeatureFlag(FEATURE_FLAG_KEYS.WEB_SHOW_IAP);
  const showSafeHarbor = useFeatureFlag(FEATURE_FLAG_KEYS.WEB_SAFE_HARBOR);
  const { isAccountant } = useProfile();

  return useMemo(() => {
    const featureFlags = {
      [FEATURE_FLAG_KEYS.WEB_SHOW_IAP]: showIAP,
      [FEATURE_FLAG_KEYS.WEB_SAFE_HARBOR]: showSafeHarbor,
    };
    const userRoles = isAccountant ? ["accountant"] : [];
    return buildFlatNavigation(featureFlags, userRoles);
  }, [showIAP, showSafeHarbor, isAccountant]);
};

/** The four slots that earn a permanent tab. Everything else lives in More. */
const TAB_HREFS = ["/dashboard", "/transactions", "/tax"];

/**
 * Bottom tab bar — under 700px only. Four destinations plus the centre green
 * add-source button; every other nav item stays reachable through More.
 */
export const MobileNav = ({
  setMobileMenuOpen,
}: {
  setMobileMenuOpen: (open: boolean) => void;
}) => {
  const location = useLocation();
  const history = useHistory();
  const navigation = useNavigation();
  const readOnly = useReadOnly();

  const hrefs = navigation.map((i) => i.href);
  const tabs = TAB_HREFS.map((href) => navigation.find((i) => i.href === href)).filter(
    Boolean
  ) as ReturnType<typeof useNavigation>;
  const on = (href: string) => isNavActive(location.pathname, href, hrefs);

  return (
    <>
      <nav className="ms-a-nav sticky bottom-0 z-20 flex items-center justify-around border-t border-ms-line bg-ms-bg px-2.5 pb-[22px] pt-2 ms700:hidden print:hidden">
        {tabs.slice(0, 2).map((item) => (
          <NavTab key={item.href} item={item} on={on(item.href)} />
        ))}

        <button
          type="button"
          disabled={readOnly}
          onClick={() => history.push("/settings/exchange/new")}
          aria-label="Add a source"
          className="-mt-5 grid h-12 w-12 flex-none place-items-center rounded-full bg-ms-pill-bg text-ms-pill-ink transition-colors hover:bg-ms-pill-bg-hover disabled:opacity-50"
        >
          <PlusIcon className="h-5 w-5" />
        </button>

        {tabs.slice(2).map((item) => (
          <NavTab key={item.href} item={item} on={on(item.href)} />
        ))}

        <button
          type="button"
          onClick={() => setMobileMenuOpen(true)}
          className="flex flex-1 flex-col items-center gap-[5px] text-ms-dim transition-colors hover:text-ms-ink"
        >
          <EllipsisHorizontalIcon className="h-5 w-5" />
          <Lab size={9} className="text-inherit">
            More
          </Lab>
        </button>
      </nav>
    </>
  );
};

const NavTab = ({
  item,
  on,
}: {
  item: { name: string; href: string; icon: any };
  on: boolean;
}) => {
  return (
    <Link
      to={item.href}
      className={clsx(
        "flex flex-1 flex-col items-center gap-[5px] transition-colors",
        on ? "text-ms-ink" : "text-ms-dim hover:text-ms-ink"
      )}
    >
      <item.icon className="h-5 w-5" />
      <Lab size={9} className="text-inherit">
        {item.name}
      </Lab>
    </Link>
  );
};

/**
 * The More sheet — every destination that does not fit the four tabs.
 * Opened from the bottom bar; unchanged routing, restyled to the document set.
 */
export const MobileMenu = ({ mobileMenuOpen, setMobileMenuOpen }) => {
  const location = useLocation();
  const navigation = useNavigation();
  const hrefs = navigation.map((i) => i.href);
  const showIAP = useFeatureFlag(FEATURE_FLAG_KEYS.WEB_SHOW_IAP);
  const { profile } = useProfile();
  const { hasActivePromo, promo, timing } = useActivePromo();

  const quizCompleted = Boolean(
    profile?.attributes?.quiz?.completedAt || localStorage.getItem("quizCompletedAt")
  );

  const close = () => setMobileMenuOpen(false);

  return (
    <Transition.Root show={mobileMenuOpen} as={Fragment}>
      <Dialog
        as="div"
        static
        className="fixed inset-0 z-40 flex ms700:hidden"
        open={mobileMenuOpen}
        onClose={setMobileMenuOpen}
      >
        <Transition.Child
          as={Fragment}
          enter="transition-opacity ease-linear duration-300"
          enterFrom="opacity-0"
          enterTo="opacity-100"
          leave="transition-opacity ease-linear duration-300"
          leaveFrom="opacity-100"
          leaveTo="opacity-0"
        >
          <Dialog.Overlay className="fixed inset-0 bg-black/50 dark:bg-black/70" />
        </Transition.Child>
        <Transition.Child
          as={Fragment}
          enter="transition ease-in-out duration-300 transform"
          enterFrom="-translate-x-full"
          enterTo="translate-x-0"
          leave="transition ease-in-out duration-300 transform"
          leaveFrom="translate-x-0"
          leaveTo="-translate-x-full"
        >
          <div className="relative flex w-full max-w-xs flex-1 flex-col overflow-y-auto border-r border-ms-line bg-ms-bg focus:outline-none">
            <div className="flex items-center justify-between border-b border-ms-rule px-[var(--pad)] py-3">
              <Link to="/" onClick={close} className="flex items-center gap-2.5">
                <Logo width={20} />
                <span className="text-[15px] font-bold tracking-[-0.015em] text-ms-ink">
                  Moonscape
                </span>
              </Link>
              <button
                type="button"
                onClick={close}
                className="grid h-10 w-10 place-items-center text-ms-mute transition-colors hover:text-ms-ink"
              >
                <span className="sr-only">Close menu</span>
                <XMarkIcon className="h-5 w-5" aria-hidden="true" />
              </button>
            </div>

            <nav aria-label="Sidebar" className="flex flex-col px-[var(--pad)] py-4">
              <Lab className="pb-[7px]">Everything</Lab>
              {navigation.map((item) => (
                <Link
                  key={item.name}
                  to={item.href}
                  onClick={close}
                  className={clsx(
                    "flex min-h-[44px] items-center gap-3 border-t border-ms-line py-[11px] text-[13.5px] tracking-[-0.01em] transition-colors first:border-t-0 hover:bg-ms-card2",
                    isNavActive(location.pathname, item.href, hrefs)
                      ? "font-semibold text-ms-ink"
                      : "text-ms-ink"
                  )}
                >
                  <item.icon className="h-5 w-5 flex-none text-ms-dim" aria-hidden="true" />
                  {item.name}
                </Link>
              ))}
              <Link
                to="/inbox"
                onClick={close}
                className="flex min-h-[44px] items-center gap-3 border-t border-ms-line py-[11px] text-[13.5px] tracking-[-0.01em] text-ms-ink transition-colors hover:bg-ms-card2"
              >
                <SparklesIcon className="h-5 w-5 flex-none text-ms-dim" aria-hidden="true" />
                Inbox
              </Link>

              {!quizCompleted && (
                <Link
                  to="/quiz"
                  onClick={close}
                  className="mt-4 flex min-h-[44px] items-center gap-3 border border-ms-line border-l-[3px] border-l-ms-rule px-3 py-[11px] text-[13.5px] font-semibold text-ms-ink transition-colors hover:bg-ms-card2"
                >
                  <SparklesIcon className="h-5 w-5 flex-none text-ms-rule" aria-hidden="true" />
                  Get started
                </Link>
              )}

              {hasActivePromo && showIAP && (
                <Link
                  to="/promo"
                  onClick={close}
                  className="mt-3 flex min-h-[44px] items-center gap-3 border border-ms-line border-l-[3px] border-l-ms-tan bg-ms-tan-soft px-3 py-[11px] text-[13.5px] font-semibold text-ms-tan"
                >
                  <SparklesIcon className="h-5 w-5 flex-none" aria-hidden="true" />
                  {promo?.discountPercent}% off — claim
                  {timing?.secondsRemaining && timing.secondsRemaining < 3600 ? (
                    <span className="ml-1 text-[11px] opacity-75">
                      ({Math.floor(timing.secondsRemaining / 60)}m)
                    </span>
                  ) : null}
                </Link>
              )}

              {showIAP && !hasActivePromo && (
                <Link
                  to="/plans"
                  onClick={close}
                  className="mt-4 inline-flex min-h-[50px] items-center justify-center gap-2.5 rounded-full bg-ms-pill-bg px-[22px] text-[15px] font-semibold tracking-[-0.02em] text-ms-pill-ink transition-colors hover:bg-ms-pill-bg-hover"
                >
                  <RocketLaunchIcon className="h-5 w-5 flex-none" aria-hidden="true" />
                  Upgrade plan
                </Link>
              )}
            </nav>
          </div>
        </Transition.Child>
        <div className="w-14 flex-shrink-0" aria-hidden="true" />
      </Dialog>
    </Transition.Root>
  );
};
